import axiosInstance from './axiosInstance';

export interface OnboardingStatus {
  _id?: string;
  userId: string;
  hasSeenWelcome: boolean;
  hasCompletedTour: boolean;
  tourStepsCompleted: string[];
  currentTourStep: number;
  milestones: {
    firstSearch: boolean;
    firstDownload: boolean;
    firstFavorite: boolean;
    firstCollection: boolean;
    firstUpload: boolean;
    usedAdvancedSearch: boolean;
    usedBulkOperations: boolean;
    viewedBrandGuide: boolean;
  };
  dismissedTips: string[];
  dismissedSpotlights: string[];
  preferences: {
    showTooltips: boolean;
    showSpotlights: boolean;
    showProgressTracker: boolean;
    showHelpButton: boolean;
  };
  onboardingStartedAt: string;
  onboardingCompletedAt?: string;
  lastInteractionAt: string;
}

export type SupportContentAudience = 'all' | 'editor' | 'admin';

export interface OnboardingContentSection {
  id: string;
  text: string;
  audience: SupportContentAudience;
}

export interface OnboardingFeatureItem {
  id: string;
  label: string;
  audience: SupportContentAudience;
}

export interface OnboardingHelpArticle {
  id: string;
  title: string;
  content: string;
  category: string;
  audience: SupportContentAudience;
}

export interface OnboardingContentSettings {
  _id?: string;
  welcomeModal: {
    title: string;
    appendUserName: boolean;
    subtitle: string;
    sections: OnboardingContentSection[];
    features: OnboardingFeatureItem[];
    primaryButtonLabel: string;
    secondaryButtonLabel: string;
    footerNote: string;
  };
  helpCenter: {
    drawerTitle: string;
    searchPlaceholder: string;
    quickActionsTitle: string;
    restartTourLabel: string;
    articlesHeading: string;
    emptySearchTitle: string;
    emptySearchDescription: string;
    footerText: string;
    categories: string[];
    articles: OnboardingHelpArticle[];
  };
  updatedAt?: string;
}

/**
 * Hämta användarens onboarding-status
 */
export async function getOnboardingStatus(): Promise<OnboardingStatus> {
  const response = await axiosInstance.get('/api/onboarding/status');
  return response.data;
}

/**
 * Hämta globalt onboarding-/supportinnehåll
 */
export async function getOnboardingContent(): Promise<OnboardingContentSettings> {
  const response = await axiosInstance.get('/api/onboarding/content');
  return response.data;
}

/**
 * Uppdatera globalt onboarding-/supportinnehåll
 */
export async function updateOnboardingContent(
  content: OnboardingContentSettings
): Promise<OnboardingContentSettings> {
  const response = await axiosInstance.put('/api/onboarding/content', content);
  return response.data;
}

/**
 * Markera att välkomst-modalen har visats
 */
export async function markWelcomeSeen(): Promise<OnboardingStatus> {
  const response = await axiosInstance.post('/api/onboarding/welcome-seen');
  return response.data;
}

/**
 * Starta guidad tour
 */
export async function startTour(): Promise<OnboardingStatus> {
  const response = await axiosInstance.post('/api/onboarding/tour/start');
  return response.data;
}

/**
 * Markera ett tour-steg som klart
 */
export async function completeTourStep(stepId: string, stepIndex: number): Promise<OnboardingStatus> {
  const response = await axiosInstance.post('/api/onboarding/tour/step', {
    stepId,
    stepIndex
  });
  return response.data;
}

/**
 * Markera hela touren som klar
 */
export async function completeTour(): Promise<OnboardingStatus> {
  const response = await axiosInstance.post('/api/onboarding/tour/complete');
  return response.data;
}

/**
 * Hoppa över touren
 */
export async function skipTour(): Promise<OnboardingStatus> {
  const response = await axiosInstance.post('/api/onboarding/tour/skip');
  return response.data;
}

/**
 * Registrera en milstolpe
 */
export async function registerMilestone(
  milestone: 'firstSearch' | 'firstDownload' | 'firstFavorite' | 'firstCollection' | 
             'firstUpload' | 'usedAdvancedSearch' | 'usedBulkOperations' | 'viewedBrandGuide'
): Promise<OnboardingStatus> {
  const response = await axiosInstance.post('/api/onboarding/milestone', { milestone });
  return response.data;
}

/**
 * Avvisa en tooltip
 */
export async function dismissTip(tipId: string): Promise<OnboardingStatus> {
  const response = await axiosInstance.post('/api/onboarding/dismiss-tip', { tipId });
  return response.data;
}

/**
 * Avvisa en feature spotlight
 */
export async function dismissSpotlight(spotlightId: string): Promise<OnboardingStatus> {
  const response = await axiosInstance.post('/api/onboarding/dismiss-spotlight', { spotlightId });
  return response.data;
}

/**
 * Uppdatera onboarding-inställningar
 */
export async function updatePreferences(
  preferences: Partial<OnboardingStatus['preferences']>
): Promise<OnboardingStatus> {
  const response = await axiosInstance.put('/api/onboarding/preferences', { preferences });
  return response.data;
}

/**
 * Återställ onboarding (för testning/visa guide igen)
 */
export async function resetOnboarding(): Promise<OnboardingStatus> {
  const response = await axiosInstance.post('/api/onboarding/reset');
  return response.data;
}

const onboardingService = {
  getOnboardingStatus,
  getOnboardingContent,
  markWelcomeSeen,
  startTour,
  completeTourStep,
  completeTour,
  skipTour,
  registerMilestone,
  dismissTip,
  dismissSpotlight,
  updatePreferences,
  resetOnboarding,
  updateOnboardingContent
};

export default onboardingService;

